import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Satellite, MapPin, RotateCcw, Save, X, Loader2, Link2, Crosshair, MousePointerClick, GitFork, CircleStop } from 'lucide-react';
import { getGnssCfg, useGnss, FIX_COLORS, FIX_SHORT, openGnss } from '../lib/gnss';
import { usePrecUi, precLevel, EXC_REASONS } from '../lib/precision';

const f2 = (v) => (+v).toFixed(2).replace('.', ',');

// survey profile exception: reason from list + free text (required for "other")
function Except({ t, p }) {
  const [k, setK] = useState('');
  const [txt, setTxt] = useState('');
  const ok = k && (k !== 'other' || txt.trim().length >= 5);
  const save = () => p.res({ reason: [t['prExcR_' + k], txt.trim()].filter(Boolean).join(' – ') });
  return (
    <>
      <h4 data-testid="prec-title">{t.prExcT}</h4>
      <p data-testid="prec-text">{(t['prExcQ_' + p.info.why] || '').replace('{v}', f2(p.info.v ?? 0)).replace('{max}', f2(p.info.max ?? 0))}</p>
      <div className="prec-exc">
        <label>{t.prExcReason}
          <select data-testid="prec-exc-reason" value={k} onChange={(e) => setK(e.target.value)}>
            <option value="">–</option>
            {EXC_REASONS.map((r) => <option key={r} value={r}>{t['prExcR_' + r]}</option>)}
          </select>
        </label>
        <label>{t.prExcNote}
          <textarea data-testid="prec-exc-note" rows={2} value={txt} onChange={(e) => setTxt(e.target.value)} />
        </label>
      </div>
      <div className="row prec-btns">
        <button className="btn btn-accent" data-testid="prec-exc-save-btn" disabled={!ok} onClick={save}><Save size={16} /> {t.prExcSave}</button>
        <button className="btn btn-outline" data-testid="prec-cancel-btn" onClick={() => p.res(null)}><X size={16} /> {t.prCancel}</button>
      </div>
    </>
  );
}

function Body({ t, p }) {
  const n = +getGnssCfg().avg || 1;
  if (p.kind === 'except') return <Except t={t} p={p} />;
  const b = (id, cls, label, val, Icon) => <button key={id} className={`btn ${cls}`} data-testid={`prec-${id}-btn`} onClick={() => p.res(val)}>{Icon && <Icon size={16} />} {label}</button>;
  if (p.kind === 'busy') return <p className="prec-busy" data-testid="prec-busy"><Loader2 size={18} className="spin" /> {t.prBusy.replace('{n}', p.info.n)}</p>;
  const M = {
    choose: [t.prChooseT, t.prChooseQ, [b('rtk', 'btn-green', t.prRtk.replace('{n}', n), 'rtk', Satellite), b('tap', 'btn-outline', t.prTap, 'tap', MapPin), b('cancel', 'btn-outline', t.prCancel, null, X)]],
    float: [t.prFloatT, t.prFloatQ, [b('ok', 'btn-accent', t.prSave, 'ok', Save), b('cancel', 'btn-outline', t.prCancel, null, X)]],
    nofix: [t.prNoFixT, t.prNoFixQ, [b('ok', 'btn-accent', t.prSave, 'ok', Save), b('cancel', 'btn-outline', t.prCancel, null, X)]],
    wind: [t.wdTitle, t.wdText.replace('{v}', p.info.v).replace('{max}', p.info.max), [b('ok', 'btn-red', t.wdStart, 'ok'), b('cancel', 'btn-outline', t.prCancel, null, X)]],
    waitH: [t.wtTitle, t.wtText.replace('{d}', p.info.d).replace('{a}', p.info.a || ''), [b('ok', 'btn-red', t.wdStart, 'ok'), b('cancel', 'btn-outline', t.prCancel, null, X)]],
    pipeStart: [t.psTitle, t.psText, (() => {
      const near = !(p.info?.d > 5), lab = `${t.psLast}${p.info?.name ? ` (${p.info.name})` : ''}${p.info?.d != null ? ` · ${p.info.d < 1000 ? Math.round(p.info.d) + ' m' : (p.info.d / 1000).toFixed(1) + ' km'}` : ''}`;
      const last = b('last', near ? 'btn-green' : 'btn-outline', lab, 'last', Link2), cross = b('cross', near ? 'btn-dark' : 'btn-green', t.psCross, 'cross', Crosshair);
      // the last object is only offered when it is within reach (50 m)
      return [...(near ? [last, cross] : p.info?.d > 50 ? [cross] : [cross, last]), b('pick', 'btn-outline', t.psPick, 'pick', MousePointerClick), b('free', 'btn-outline', t.psFree, null, X)];
    })()],
    pipeEnd: [t.peTitle, t.peText, [b('last', 'btn-green', `${t.peNear}${p.info.name ? ` (${p.info.name})` : ''}`, 'last', Link2), b('cross', 'btn-dark', t.peCross, 'cross', Crosshair), b('pick', 'btn-outline', t.pePick, 'pick', MousePointerClick), b('free', 'btn-outline', t.peFree, 'free', X)]],
    endFix: [t.oeFixT, t.oeFixQ.replace('{name}', p.info.pipe || ''), [p.info.near && b('near', 'btn-green', t.oeNear.replace('{name}', p.info.near).replace('{d}', p.info.d), 'near', Link2), p.info.tee && b('tee', 'btn-dark', t.oeTee, 'tee', GitFork), b('pick', 'btn-outline', t.psPick, 'pick', MousePointerClick), b('cap', 'btn-outline', t.oeCap, 'cap', CircleStop), b('cancel', 'btn-outline', t.prCancel, null, X)].filter(Boolean)],
    blocked: [t.prBlockT, p.info.lost ? t.gnssNoFix : p.info.survey ? t.prSurveyBlockQ : t.prBlockQ, [b('ok', 'btn-dark', t.prOk, 'ok')]],
    fixlost: [t.prFixLostT, p.info.lost ? t.prFixLostQ : t.prFixChgQ.replace('{fix}', FIX_SHORT[p.info.fix] || p.info.fix || ''), [b('retry', 'btn-green', t.prRetry, 'retry', RotateCcw), b('cancel', 'btn-outline', t.prCancel, null, X)]],
    retake: [t.prRetakeT, t.prRetakeQ.replace('{v}', f2(p.info.v)).replace('{max}', f2(p.info.max)), [b('retry', 'btn-green', t.prRetry, 'retry', RotateCcw), b('ok', 'btn-outline', t.prSaveAny, 'ok', Save), b('cancel', 'btn-outline', t.prCancel, null, X)]],
  }[p.kind];
  return (
    <>
      <h4 data-testid="prec-title">{M[0]}</h4>
      <p data-testid="prec-text">{M[1]}</p>
      <div className="row prec-btns">{M[2]}</div>
    </>
  );
}

// modal for the RTK precision engine
export default function PrecisionDialog({ t }) {
  const p = usePrecUi();
  if (!p) return null;
  return createPortal(
    <div className="editor-backdrop prec-backdrop" data-testid="prec-dialog">
      <div className={`editor prec-box prec-${p.kind}`}><Body t={t} p={p} /></div>
    </div>,
    document.body,
  );
}

// compact persistent RTK status badge ("RTK FIX 0,02 m")
export function RtkBadge({ t, testid = 'rtk-badge' }) {
  const s = useGnss();
  const lv = precLevel(s), fix = lv === 'none' ? 'none' : s.fix;
  const acc = s.hrms != null && lv !== 'none' ? ` ${s.hrms < 1 ? f2(s.hrms) : s.hrms.toFixed(1)} m` : '';
  return (
    <button className={`rtk-badge rtk-${lv}`} style={{ '--fix': FIX_COLORS[fix] }} data-testid={testid} onClick={openGnss} title={t.gnssTitle} aria-label={t.gnssTitle}>
      <i />{lv === 'none' ? t.prBadgeOff : `${FIX_SHORT[fix]}${acc}`}
    </button>
  );
}
